import { Link } from "react-router-dom";
import { Footer } from "@/components/Footer";
import { BookOpen, ShieldCheck, RefreshCw, Users, AlertCircle, FileText, Copyright } from "lucide-react";

const EditorialStandards = () => {
  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
            How We Work
          </p>
          <h1 className="font-serif text-4xl md:text-5xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Editorial &amp; Medical Standards
          </h1>
          <p className="text-muted-foreground italic text-lg leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Where our information comes from, how we keep it accurate, and how we respect the sources we consult.
          </p>
        </div>

        <section className="mb-10 space-y-4">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Our approach</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground/90">
            Her Season creates <strong>original educational content</strong> informed by recognised
            clinical guidance. Our editorial and clinical review process checks important health
            information against trusted sources and records the evidence used for each article.
          </p>
          <p className="text-sm leading-relaxed text-foreground/90">
            The reference sources we consult include:
          </p>
          <ul className="list-disc pl-6 text-sm space-y-1 text-foreground/90">
            <li><strong>NICE NG23</strong> — UK Menopause Guideline</li>
            <li><strong>NAMS / The Menopause Society</strong> — Hormone Therapy Position Statement</li>
            <li><strong>Australasian Menopause Society (AMS)</strong> — Information sheets</li>
            <li><strong>Jean Hailes for Women's Health</strong> — Consumer-facing guidance</li>
            <li><strong>RANZCOG</strong> — Menopause and MHT position statements</li>
          </ul>
          <div className="rounded-md border border-muted p-4 bg-muted/30 text-sm text-foreground/90">
            <p>
              <strong>“Clinical sources reviewed”</strong> means that the source was consulted during
              the development or verification of Her Season content. It does <em>not</em> mean the
              original source has been republished, or that the source organisation has endorsed
              Her Season.
            </p>
          </div>
        </section>

        <section className="mb-10 space-y-4">
          <div className="flex items-center gap-3">
            <Copyright className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Copyright &amp; source use</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground/90">
            External organisations referenced by Her Season do not necessarily endorse, sponsor, or
            approve the app. Publicly available source material remains the property of its
            respective owner. Her Season does not reproduce substantial source content, tables,
            graphics, or publications unless permission or an appropriate licence has been
            confirmed.
          </p>
          <p className="text-sm leading-relaxed text-foreground/90">
            We use trusted guidance to support and verify our own writing — not to republish it.
            Every claim on the site is written in Her Season's own words and, where relevant,
            traceable to the source that supports it.
          </p>
        </section>

        <section className="mb-10 space-y-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Content grading</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground/90">
            Each article and symptom entry carries one of three grades:
          </p>
          <ul className="space-y-3 text-sm">
            <li className="border-l-2 border-primary/40 pl-4">
              <strong>Guideline-backed</strong> — Original Her Season content checked against a
              specific current clinical guideline. Sources consulted are listed and citation
              records are kept internally.
            </li>
            <li className="border-l-2 border-primary/40 pl-4">
              <strong>Clinician-reviewed</strong> — Reviewed by a qualified healthcare professional
              before publication. Reviewer name, credentials and date are recorded.
            </li>
            <li className="border-l-2 border-primary/40 pl-4">
              <strong>Editorial</strong> — Lifestyle, community, practical support and wellbeing
              content written by our editorial team. It does not claim clinical authority and is
              not a substitute for individual medical advice.
            </li>
            <li className="border-l-2 border-amber-400/60 pl-4">
              <strong>Editorial — pending clinical review</strong> — Content that has not yet
              completed clinical review. It cannot be labelled guideline-backed.
            </li>
          </ul>
        </section>

        <section className="mb-10 space-y-4">
          <div className="flex items-center gap-3">
            <RefreshCw className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Review cadence</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground/90">
            Guideline-backed content is reviewed at least every 12 months, immediately when a
            referenced guideline is updated, and whenever a safety concern is raised. Every
            published entry records the date of last review and the reviewer. Superseded or
            expired guidance is removed from our active knowledge base until reviewed.
          </p>
        </section>

        <section className="mb-10 space-y-4">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Aura AI &amp; how it answers</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground/90">
            Aura is a supportive companion, not a clinician. It writes in original language and
            does not reproduce source wording. It labels every reply as one of{" "}
            <em>Evidence-based guidance</em>, <em>General educational information</em>, or{" "}
            <em>Medical review recommended</em>, and lists the organisations whose guidance was
            consulted when relevant. Aura does not diagnose, prescribe, or imply endorsement by any
            external organisation. Your conversations are not shared with third parties beyond the
            AI model provider needed to generate a response.
          </p>
        </section>

        <section className="mb-10 space-y-4">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Report a concern</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground/90">
            If you spot outdated or inaccurate information, please email{" "}
            <a href="mailto:hello@herseason.com" className="text-primary underline">
              hello@herseason.com
            </a>
            . We take corrections seriously and will respond within 5 business days.
          </p>
        </section>

        <section className="mb-4 space-y-4 rounded-lg border border-primary/20 bg-primary/5 p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Important</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground/90">
            Her Season provides general educational information and does not replace personalised
            medical advice, diagnosis, or treatment. Clinical references are used to support and
            verify our original content. Mention of an organisation does not imply endorsement,
            sponsorship, or approval of Her Season. Always speak with a qualified healthcare
            professional before starting, stopping, or changing any treatment.
          </p>
        </section>

        <div className="mt-10 text-sm">
          <Link to="/" className="text-primary hover:underline">
            ← Back to home
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EditorialStandards;
